import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { IKPIRequestDataModel, KPIRequestStatus } from '../../../../../data-models/kpi-requests.data-model';
import { kpiRequestsGridSchema } from '../../../../../data-schemas/KPIRequestSchema';
import { getIsoDateColDef } from '../../../../../util/ag-grid-utils';
import { useSchemaToGrid } from '../../../../../util/schema-utils';
import { ArchivedRequestsActions } from '../components/ArchivedRequestsActions';
import { SentAtRenderer } from '../components/SentAtRenderer';
import { SentRequestActions } from '../components/SentRequestActions';
import { useCommonKpiColDefs } from './useCommonKpiColDefs';

export default function useKPIRequestsSentColDefs(
  status: typeof KPIRequestStatus.Sent | typeof KPIRequestStatus.Archived
) {
  const {
    commonCompanyDefs,
    respondentColDef,
    periodColDef,
    frequencyColDef,
    granularityColDef,
    templateColDef,
    fyeColDef,
    secondaryRespondentColDef,
  } = useCommonKpiColDefs();
  const schemaToGrid = useSchemaToGrid();
  const reqSchema = kpiRequestsGridSchema();
  const columnDefs: ColDef<IKPIRequestDataModel>[] = useMemo(() => {
    const res = [commonCompanyDefs];
    const [dueDate, archivedAt] = schemaToGrid(reqSchema, ['dueDate', 'archivedAt']) as ColDef[];
    if (status === KPIRequestStatus.Sent) {
      res.push(
        {
          headerName: 'Sent At',
          cellRenderer: SentAtRenderer,
          minWidth: 180,
          field: 'sentAt',
          ...getIsoDateColDef(),
        } as ColDef,
        {
          ...dueDate,
          ...getIsoDateColDef(),
        } as ColDef
      );
    } else if (status == KPIRequestStatus.Archived) {
      res.push({ ...archivedAt, ...getIsoDateColDef() } as ColDef);
    }
    res.push(
      ...([
        templateColDef,
        frequencyColDef,
        fyeColDef,
        periodColDef,
        respondentColDef,
        secondaryRespondentColDef,
        {
          ...(status === KPIRequestStatus.Sent
            ? { minWidth: 130, maxWidth: 130, cellStyle: { paddingLeft: 10, paddingRight: 10 } }
            : { maxWidth: 60 }),
          resizable: false,
          sortable: false,
          pinned: 'right',
          cellRenderer: status === KPIRequestStatus.Sent ? SentRequestActions : ArchivedRequestsActions,
        },
      ] as ColDef<IKPIRequestDataModel>[])
    );

    res.splice(4, 0, granularityColDef as unknown as ColDef<IKPIRequestDataModel>);

    return res;
  }, [
    frequencyColDef,
    fyeColDef,
    granularityColDef,
    commonCompanyDefs,
    periodColDef,
    reqSchema,
    respondentColDef,
    schemaToGrid,
    secondaryRespondentColDef,
    status,
    templateColDef,
  ]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      flex: 1,
      minWidth: 120,
      resizable: true,
      sortable: true,
      filter: false,
      suppressHeaderMenuButton: true,
      suppressMovable: true,
      getQuickFilterText: () => {
        return '';
      },
    }),
    []
  );

  return {
    columnDefs,
    defaultColDef,
  };
}
